import {
  Badge,
  Button,
  cn,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  focusStyles,
  TimeIndicator,
  TimeIndicatorDot,
  TimeIndicatorLabel,
} from "@beweegbaas/ui";
import { getLocalTimeZone, parseDate, today, type CalendarDate } from "@internationalized/date";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  retainSearchParams,
  stripSearchParams,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useState, type ComponentProps, type ReactElement, type ReactNode } from "react";
import { LessonsHeader } from "#/components/lessons-header";
import { containerStyles, Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { DocentFilter } from "#/modules/les.planning/components/docent-filter";
import {
  AdministratiefInhoud,
  celKlassen,
  LesnummerLabel,
  StatusLabel,
  tegelHoverClassName,
  tegelStatusClassName,
} from "#/modules/les.planning/components/les-tegel";
import { LeerlijnDot, ThemaLijst } from "#/modules/les.planning/components/leerlijn-dot";
import { BLEED, PAUZE_ACHTERGROND } from "#/modules/les.planning/components/lesuren-week";
import { PlanningToolbar } from "#/modules/les.planning/components/planning-toolbar";
import { WeekLessen } from "#/modules/les.planning/components/week-lessen";
import {
  clampToSchooljaar,
  getIsoWeekNumber,
  getLesdag,
  getNuPositie,
  getSchooljaarRange,
  getWeekStart,
  minutesToLabel,
  parseDateParam,
} from "#/modules/les.planning/domain/calendar";
import { getPlanningRange } from "#/modules/les.planning/domain/les";
import {
  EMPTY_LESROOSTER,
  formatLesuur,
  type Lesuur,
} from "#/modules/les.planning/domain/lesrooster";
import { hasOwnRooster } from "#/modules/les.planning/domain/rooster";
import {
  getLesSociaalThemas,
  getLesThemas,
  getLesurenGrid,
  getWeekLessen,
  isAdministratief,
  type Cel,
} from "#/modules/les.planning/domain/week";
import {
  jaarplanningQueryOptions,
  planningQueryOptions,
  roosterQueryOptions,
} from "#/modules/les.planning/query.planning";
import { getStepTo } from "#/modules/les.planning/preparation-steps";
import { RoosterButton } from "#/modules/les.planning/rooster-route";
import { LESDAG_SEARCH_DEFAULTS, lesdagSearchSchema } from "#/modules/les.planning/schema.search";
import { startLessonFlowSearchDefaults } from "#/modules/les.start-flow/filters";
import {
  getBeweegThemaTranslation,
  getLeerlijnTranslation,
} from "#/modules/util.i18n/enum-mapping";

// Where a docent's day starts: today's lesuren, where a les starts. At the weekend it is the
// Monday after; the docent steps to other days like in Mijn lesweek. A vakantie or vrije dag
// shows only that it is one: Mijn lesweek is where a les still gets planned on it.
export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lessons/today",
)({
  validateSearch: lesdagSearchSchema,
  // The colleagues go along to Mijn lesweek and back.
  search: {
    middlewares: [stripSearchParams(LESDAG_SEARCH_DEFAULTS), retainSearchParams(["docenten"])],
  },
  loaderDeps: ({ search }) => ({
    week: getWeekStart(getLesdag(parseDateParam(search.date))).toString(),
  }),
  loader: async ({ context, params: { school, schoolyear }, deps }) => {
    const tenant = { school, schoolyear };
    const weekStart = parseDate(deps.week);
    await Promise.all([
      context.queryClient.ensureQueryData(jaarplanningQueryOptions(tenant)),
      context.queryClient.ensureQueryData(roosterQueryOptions(tenant)),
      context.queryClient.ensureQueryData(
        planningQueryOptions(tenant, getPlanningRange(weekStart)),
      ),
    ]);
  },
  component: LesdagPage,
});

const weekdayFormatter = new Intl.DateTimeFormat("nl-NL", { weekday: "long" });
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function LesdagPage() {
  const { locale, school, schoolyear } = Route.useParams();
  // Its own params only: a dialog over the page keeps its draft in the URL as well, and every
  // change to it would render the whole day again.
  const search = Route.useSearch({ select: ({ date, docenten }) => ({ date, docenten }) });
  const navigate = Route.useNavigate();
  const tenant = { school, schoolyear };
  const params = { locale, school, schoolyear };
  const router = useRouter();
  const timeZone = getLocalTimeZone();

  // Statuses follow the clock: a les that is over turns green or grey.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const range = getSchooljaarRange(schoolyear);
  const vandaag = today(timeZone);
  const day = clampToSchooljaar(getLesdag(parseDateParam(search.date, timeZone)), range);
  const isVandaag = day.compare(vandaag) === 0;
  const weekStart = getWeekStart(day);

  const jaarplanning = useQuery(jaarplanningQueryOptions(tenant));
  const planning = useQuery({
    ...planningQueryOptions(tenant, getPlanningRange(weekStart, timeZone)),
    placeholderData: keepPreviousData,
  });
  const rooster = useQuery(roosterQueryOptions(tenant));
  const regels = rooster.data?.regels ?? [];
  const docenten = rooster.data?.docenten ?? [];
  // Until the docent's lessen are in the rooster, any lesuur starts a les and its time says little.
  const ownRooster = hasOwnRooster(regels);

  const grid = getLesurenGrid({
    lesrooster: rooster.data?.lesrooster ?? EMPTY_LESROOSTER,
    weekStart,
    days: [day],
    lessen: planning.data?.lessen ?? [],
    regels,
    docenten,
    colleagueIds: search.docenten,
    vakanties: jaarplanning.data?.vakanties ?? [],
    now,
    timeZone,
    // A lesuur whose time has passed still starts: the les was given after all.
    keepEndedSlots: true,
  });
  const dayKey = day.toString();
  const vakantie = grid.dagen[0]?.vakantie ?? null;
  const cellen = grid.rijen.flatMap((rij) =>
    rij.kind === "lesuur" ? (rij.cellen[dayKey] ?? []) : [],
  );
  const hasLesuren = grid.rijen.some(
    (rij) =>
      rij.kind === "lesuur" &&
      (rij.lesuren[dayKey] !== undefined || (rij.cellen[dayKey]?.length ?? 0) > 0),
  );
  const weekLessen = getWeekLessen({
    jaarplanning: jaarplanning.data,
    weekStart,
    regels,
    today: day,
  }).filter((les) => cellen.some((cel) => cel.lesnummer === les.les));
  const minutes = now.getHours() * 60 + now.getMinutes();
  const nuMinutes = isVandaag ? minutes : null;
  const isNu = (lesuur: Lesuur) =>
    getNuPositie(lesuur.startMinuten, lesuur.duurMinuten, nuMinutes) !== null;
  // Before the day's first lesuur and after its last, the time of now waits at that edge of
  // the day: at the top of its first row in the morning, at the bottom of its last at night.
  const dagRijen = grid.rijen.flatMap((rij) => {
    const tijd = rij.kind === "pauze" ? rij.pauzes[dayKey] : rij.lesuren[dayKey];
    return tijd
      ? [{ key: rij.key, start: tijd.startMinuten, eind: tijd.startMinuten + tijd.duurMinuten }]
      : [];
  });
  const eersteRij = dagRijen[0];
  const laatsteRij = dagRijen.at(-1);
  const randNu =
    nuMinutes === null || !eersteRij || !laatsteRij
      ? null
      : nuMinutes < eersteRij.start
        ? { key: eersteRij.key, positie: 0 }
        : nuMinutes >= laatsteRij.eind
          ? { key: laatsteRij.key, positie: 1 }
          : null;
  const nuPositie = (key: string, positie: number | null) =>
    positie ?? (randNu?.key === key ? randNu.positie : null);
  const nuRand = (key: string, positie: number | null) =>
    positie === null && randNu?.key === key ? (randNu.positie === 0 ? "boven" : "onder") : null;
  const weekdag = weekdayFormatter.format(day.toDate(timeZone));

  function setDate(next: CalendarDate) {
    void navigate({
      search: (prev) => ({ ...prev, date: clampToSchooljaar(next, range).toString() }),
      replace: true,
    });
  }

  function setColleagues(colleagueIds: string[]) {
    void navigate({
      search: (prev) => ({ ...prev, docenten: colleagueIds }),
      replace: true,
      resetScroll: false,
    });
  }

  function naarLes(cel: Cel) {
    if (cel.kind !== "les") return;
    void router.navigate({
      to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores",
      params: { ...params, lesson: cel.les.id },
    });
  }

  function voorbereiden(cel: Cel, lesuur: Lesuur | null) {
    void router.navigate({
      from: Route.fullPath,
      // A les opens on its groepjes, where it starts; a roosteruur on what it is about.
      to: getStepTo("lesdag", cel.kind === "les" ? "groups" : "activities"),
      params,
      // The day and the colleagues that show stay under the dialog.
      search: (prev) => ({
        ...prev,
        ...startLessonFlowSearchDefaults,
        ...(cel.kind === "les"
          ? { editLesId: cel.les.id }
          : {
              klasIds: cel.slot.klassen.map((klas) => klas.id),
              roosterregelId: cel.slot.regel.id,
              scheduledDate: cel.slot.day.toString(),
              scheduledTime: minutesToLabel(cel.slot.regel.startMinuten),
              duurMinuten: cel.slot.regel.duurMinuten,
              lesuurId: lesuur?.id ?? null,
              schooldocentId: cel.colleague?.schooldocentId ?? null,
            }),
      }),
    });
  }

  return (
    <Page>
      <LessonsHeader actions={null} />
      <Page.Body className={cn(containerStyles({ container: "default" }), "mt-4 pb-10")}>
        <PendingContent pending={planning.isFetching}>
          <div className="flex flex-col" data-testid="lesdag">
            {/* As in Mijn lesweek; the line under it is the top of the lesuren. */}
            <PlanningToolbar
              view="day"
              date={day}
              weekStart={weekStart}
              range={range}
              addon={
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-muted-foreground text-sm">
                    {m["lessen.lesdag.week"]({ week: getIsoWeekNumber(weekStart) })}
                  </span>
                  {isVandaag ? (
                    <Badge variant="blue" size="sm" data-vandaag>
                      {m["planning.vandaag"]()}
                    </Badge>
                  ) : null}
                </span>
              }
              className="pb-4"
              onDateChange={setDate}
              actions={
                <>
                  <RoosterButton
                    render={
                      <Link
                        to="/$locale/{-$schoolyear}/$school/lessons/today/rooster"
                        params={params}
                        search={true}
                      />
                    }
                  />
                  <DocentFilter
                    docenten={docenten}
                    colleagueIds={search.docenten}
                    onChange={setColleagues}
                  />
                </>
              }
            />
            {vakantie !== null ? (
              <DayNotice
                className="bg-muted"
                icon={<IconMaterialSymbolsEventBusyOutline />}
                heading={vakantie}
                action={
                  <NoticeButton
                    render={
                      <Link
                        to="/$locale/{-$schoolyear}/$school/lessons/today/vakanties"
                        params={params}
                        search={true}
                      />
                    }
                  >
                    {m["lessen.lesdag.vakanties"]()}
                  </NoticeButton>
                }
                data-vrije-dag={vakantie}
              />
            ) : ownRooster ? null : (
              <DayNotice
                className="bg-primary/[0.06]"
                heading={m["lessen.lesdag.geen_rooster"]()}
                description={m["lessen.lesdag.geen_rooster_uitleg"]()}
                action={
                  <NoticeButton
                    render={
                      <Link
                        to="/$locale/{-$schoolyear}/$school/lessons/today/rooster"
                        params={params}
                        search={true}
                      />
                    }
                  >
                    {m["lessen.lesdag.rooster_invullen"]()}
                  </NoticeButton>
                }
                data-without-rooster
              />
            )}
            {vakantie !== null ? null : hasLesuren ? (
              <>
                <WeekLessen
                  lessen={weekLessen}
                  className={cn(BLEED, "border-t py-3")}
                  labelClassName="sm:w-42"
                />
                <ol className="m-0 flex list-none flex-col p-0 *:last:border-b">
                  {grid.rijen.map((rij) => {
                    if (rij.kind === "pauze") {
                      const pauze = rij.pauzes[dayKey];
                      const positie = pauze
                        ? getNuPositie(pauze.startMinuten, pauze.duurMinuten, nuMinutes)
                        : null;
                      return pauze ? (
                        <li
                          key={rij.key}
                          className={cn(
                            BLEED,
                            "text-muted-foreground relative flex h-6 items-center gap-2.5 border-t text-[11px] tabular-nums sm:gap-0",
                            PAUZE_ACHTERGROND,
                          )}
                          data-pauze={pauze.naam}
                        >
                          <span className="w-16 shrink-0 sm:w-30">
                            {ownRooster ? minutesToLabel(pauze.startMinuten) : null}
                          </span>
                          <NuBaan
                            minutes={nuMinutes}
                            positie={nuPositie(rij.key, positie)}
                            rand={nuRand(rij.key, positie)}
                            className="sm:h-full sm:w-20"
                            indicatorClassName="sm:left-2"
                          />
                          <span className="text-xs">{pauze.naam}</span>
                        </li>
                      ) : null;
                    }
                    const lesuur = rij.lesuren[dayKey] ?? null;
                    const rijCellen = rij.cellen[dayKey] ?? [];
                    if (rijCellen.length === 0 && lesuur === null) return null;
                    const positie = lesuur
                      ? getNuPositie(lesuur.startMinuten, lesuur.duurMinuten, nuMinutes)
                      : null;
                    return (
                      <li
                        key={rij.key}
                        className={cn(
                          BLEED,
                          "relative flex items-stretch gap-2.5 border-t py-1 sm:gap-2",
                        )}
                        data-lesuur={rij.nummer}
                      >
                        {/* On a phone the time of now runs behind it: each line masks it with its own background. */}
                        <span className="relative z-20 flex w-16 shrink-0 flex-col items-start justify-center *:max-sm:bg-background *:max-sm:pr-1 sm:w-30">
                          <span
                            className={cn(
                              "font-heading text-[15px] leading-tight font-extrabold",
                              rijCellen.length === 0 && "text-muted-foreground",
                              positie !== null && "text-primary",
                            )}
                          >
                            {formatLesuur({ nummer: rij.nummer })}
                          </span>
                          {lesuur && ownRooster ? (
                            <span className="text-muted-foreground text-xs tabular-nums">
                              {minutesToLabel(lesuur.startMinuten)} –{" "}
                              {minutesToLabel(lesuur.startMinuten + lesuur.duurMinuten)}
                            </span>
                          ) : null}
                        </span>
                        <NuBaan
                          minutes={nuMinutes}
                          positie={nuPositie(rij.key, positie)}
                          rand={nuRand(rij.key, positie)}
                          // Beyond the 4px the row has around this lane lies its border.
                          className="sm:w-16 sm:[--nu-boven:-4px] sm:[--nu-onder:calc(100%+4px)]"
                          indicatorClassName="sm:-right-2"
                          dot
                        />
                        <span className="@container flex min-w-0 flex-1 flex-col gap-1">
                          {rijCellen.length === 0 && lesuur ? (
                            <span className="flex min-h-10 items-center gap-x-6 text-sm">
                              <span className="text-muted-foreground w-32 shrink-0">
                                {m["lessen.lesdag.geen_les_gepland"]()}
                              </span>
                              <Button
                                variant="link"
                                className="h-auto px-0 font-semibold"
                                nativeButton={false}
                                render={
                                  <Link
                                    from={Route.fullPath}
                                    to={getStepTo("lesdag", "class")}
                                    params={params}
                                    search={(prev) => ({
                                      ...prev,
                                      ...startLessonFlowSearchDefaults,
                                      klasIds: [],
                                      scheduledDate: dayKey,
                                      scheduledTime: minutesToLabel(lesuur.startMinuten),
                                      duurMinuten: lesuur.duurMinuten,
                                      lesuurId: lesuur.id,
                                    })}
                                  />
                                }
                                data-les-plannen
                              >
                                <IconMaterialSymbolsAdd />
                                {m["planning.week.les_plannen"]()}
                              </Button>
                            </span>
                          ) : (
                            rijCellen.map((cel) => (
                              <LesdagKaart
                                key={cel.key}
                                cel={cel}
                                minutenOver={
                                  lesuur !== null && isNu(lesuur) ? cel.eindMinuten - minutes : null
                                }
                                onNaarLes={() => naarLes(cel)}
                                onVoorbereiden={() => voorbereiden(cel, lesuur)}
                              />
                            ))
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>
                    {isVandaag
                      ? m["lessen.lesdag.geen_lessen"]()
                      : m["lessen.lesdag.geen_lessen_op"]({ dag: capitalize(weekdag) })}
                  </EmptyTitle>
                  <EmptyDescription>
                    {isVandaag
                      ? m["lessen.lesdag.geen_lessen_uitleg"]()
                      : m["lessen.lesdag.geen_lessen_op_uitleg"]({ dag: weekdag })}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button
                    variant="outline"
                    size="xl"
                    nativeButton={false}
                    render={
                      <Link to="/$locale/{-$schoolyear}/$school/lessons/week" params={params} />
                    }
                  >
                    {m["lessen.lesweek.tab"]()}
                  </Button>
                </EmptyContent>
              </Empty>
            )}
          </div>
        </PendingContent>
      </Page.Body>
      <Outlet />
    </Page>
  );
}

/**
 * The lane between the lesuren and their lessen that only the time of now uses, so it never
 * covers a lesuur's name or a les. A phone has no room for it: there it runs over the lesuur's
 * name up to the les.
 */
function NuBaan({
  minutes,
  positie,
  rand,
  dot,
  className,
  indicatorClassName,
}: {
  minutes: number | null;
  positie: number | null;
  /** Before or after the day's lesuren: on the line above or below this row, not inside it. */
  rand?: "boven" | "onder" | null;
  dot?: boolean;
  className?: string;
  indicatorClassName?: string;
}) {
  return (
    <span
      className={cn(
        "shrink-0 max-sm:absolute max-sm:inset-y-0 max-sm:left-0 max-sm:w-[calc(var(--container-gutter)+4.625rem)] sm:relative",
        className,
      )}
    >
      {minutes !== null && positie !== null ? (
        <TimeIndicator
          position={positie}
          style={
            rand
              ? { top: rand === "boven" ? "var(--nu-boven, 0px)" : "var(--nu-onder, 100%)" }
              : undefined
          }
          className={indicatorClassName}
          data-nu={minutesToLabel(minutes)}
        >
          {/* A phone has no room for the time: the line and the blue lesuur say it. */}
          <TimeIndicatorLabel className="max-sm:hidden">
            {minutesToLabel(minutes)}
          </TimeIndicatorLabel>
          {dot ? <TimeIndicatorDot /> : null}
        </TimeIndicator>
      ) : null}
    </span>
  );
}

/** What is different about the day, above its lesuren, with where to change it. */
function DayNotice({
  icon,
  heading,
  description,
  action,
  className,
  ...props
}: {
  icon?: ReactNode;
  heading: string;
  description?: string;
  action: ReactNode;
} & ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "mb-4 flex flex-col items-start gap-3 squircle rounded-xl supports-squircle:rounded-3xl px-5 py-4 sm:flex-row sm:items-center sm:gap-x-4",
        className,
      )}
      {...props}
    >
      <span className="flex w-full min-w-0 items-center gap-3 text-sm sm:flex-1">
        {icon ? (
          <span aria-hidden className="text-muted-foreground flex shrink-0 [&_svg]:size-5">
            {icon}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-semibold">{heading}</span>
          {description ? <span className="text-muted-foreground">{description}</span> : null}
        </span>
      </span>
      {action}
    </div>
  );
}

function NoticeButton({ render, children }: { render: ReactElement; children: ReactNode }) {
  return (
    <Button
      variant="outline"
      size="lg"
      className="bg-background shrink-0"
      nativeButton={false}
      render={render}
    >
      <IconMaterialSymbolsCalendarMonthOutline />
      {children}
    </Button>
  );
}

/** A les of the day with what it is about and what the docent can do with it now. */
function LesdagKaart({
  cel,
  minutenOver,
  onNaarLes,
  onVoorbereiden,
}: {
  cel: Cel;
  /** While its lesuur runs: how many minutes it has left. */
  minutenOver: number | null;
  onNaarLes: () => void;
  onVoorbereiden: () => void;
}) {
  const nu = minutenOver !== null;
  // The card opens its les: a given or running one where it runs, any other in its preparation.
  const open = cel.status === "gegeven" || cel.status === "bezig" ? onNaarLes : onVoorbereiden;
  const onderdelen =
    cel.kind === "les" ? [...cel.les.onderdelen].sort((a, b) => a.volgorde - b.volgorde) : [];
  const sociaal = cel.kind === "les" ? getLesSociaalThemas(cel.les) : [];
  const administratief = cel.kind === "les" && isAdministratief(cel.les);
  // With doelen or without, a les is ready to give: only a roosteruur still needs preparing.
  const prepared = cel.kind === "les";
  const toCome = cel.status === "gepland" || cel.status === "voorbereiden";
  const startsNow = nu && toCome;
  const colleague = cel.colleague;
  const firstName = cel.colleagueName ?? "";
  // A roosteruur whose time has passed reads as past, and still opens as a coming one does.
  const missed = cel.status === "gemist" && !prepared;
  const notPrepared = toCome ? (
    <span className="text-muted-foreground">{m["lessen.niet_voorbereid"]()}</span>
  ) : cel.status === "gemist" ? (
    <span className="text-muted-foreground">
      {prepared ? m["lessen.niet_voorbereid"]() : m["lessen.niet_gegeven"]()}
    </span>
  ) : null;
  const status =
    cel.status === "gegeven" || cel.status === "bezig" ? (
      <StatusLabel status={cel.status} className="text-sm" />
    ) : startsNow ? (
      <span className="text-muted-foreground text-sm tabular-nums">
        {m["lessen.lesdag.nog_minuten"]({ minuten: minutenOver })}
      </span>
    ) : (toCome || missed) && !colleague && !prepared ? (
      // The card itself opens the preparation; this only says so.
      <span className="text-primary py-1 text-sm font-semibold @2xl:px-2">
        {missed ? m["lessen.lesdag.alsnog_invullen"]() : m["lessen.lesdag.voorbereiden"]()}
      </span>
    ) : null;
  // On a narrow card the button takes its full width under what the les is about.
  const actionClassName = "relative col-span-2 @max-2xl:w-full";
  return (
    <div
      className={cn(
        "relative grid min-h-21.5 grid-cols-[minmax(0,1fr)_auto] content-center items-center gap-x-3 gap-y-2.5 squircle rounded-xl supports-squircle:rounded-3xl px-4 py-3.5 transition-[background-color,border-color] @2xl:flex @2xl:min-h-20 @2xl:flex-wrap @2xl:gap-x-6 @2xl:gap-y-2 @2xl:px-5 @2xl:py-3",
        tegelStatusClassName[cel.status],
        tegelHoverClassName[cel.status],
      )}
      data-cel-status={cel.status}
      data-les-id={cel.kind === "les" ? cel.les.id : undefined}
      data-roosterslot={cel.kind === "slot" ? cel.slot.key : undefined}
      data-lesnummer={cel.lesnummer ?? undefined}
      data-colleague={colleague?.schooldocentId}
    >
      <button
        type="button"
        onClick={open}
        aria-label={m["lessen.lesdag.les_openen"]({ klas: celKlassen(cel) })}
        className={cn(
          "absolute inset-0 cursor-pointer squircle rounded-xl supports-squircle:rounded-3xl",
          focusStyles,
        )}
        data-kaart
      />
      <span className="pointer-events-none flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 @2xl:min-w-32 @2xl:shrink-0 @2xl:flex-col @2xl:items-start @2xl:gap-1">
        <span
          className={cn(
            // The klas matters most: on a narrow card it wraps rather than being cut off.
            "font-heading min-w-0 text-xl font-extrabold wrap-break-word @2xl:whitespace-nowrap",
            cel.status === "gegeven" || missed ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {celKlassen(cel)}
        </span>
        {cel.lesnummer !== null ? <LesnummerLabel nummer={cel.lesnummer} /> : null}
      </span>
      <span
        className={cn(
          "pointer-events-none col-span-2 flex min-w-0 flex-col gap-0.5 text-sm @2xl:flex-1",
          // On a narrow card the link to prepare it says enough.
          !colleague && !prepared && "@max-2xl:hidden",
        )}
      >
        {colleague ? (
          <>
            <span className="truncate font-semibold">
              {m["lessen.collega.les_van"]({ naam: firstName })}
            </span>
            {startsNow ? (
              <span className="text-muted-foreground">
                {m["lessen.collega.start_uitleg"]({ naam: firstName })}
              </span>
            ) : administratief ? (
              <AdministratiefInhoud />
            ) : cel.kind === "les" ? (
              <ThemaLijst themas={getLesThemas(cel.les)} />
            ) : (
              notPrepared
            )}
          </>
        ) : administratief ? (
          <AdministratiefInhoud />
        ) : prepared ? (
          <>
            {onderdelen.map((onderdeel) => (
              <span key={onderdeel.id} className="flex min-w-0 items-center gap-2">
                <LeerlijnDot leerlijn={onderdeel.leerlijn} />
                <span className="shrink-0 font-semibold">
                  {onderdeel.beweegthema
                    ? getBeweegThemaTranslation(onderdeel.beweegthema)
                    : onderdeel.leerlijn
                      ? getLeerlijnTranslation(onderdeel.leerlijn)
                      : null}
                </span>
                {onderdeel.beweegactiviteitName ? (
                  <span className="text-muted-foreground truncate">
                    {onderdeel.beweegactiviteitName}
                  </span>
                ) : null}
              </span>
            ))}
            {sociaal.length > 0 ? <ThemaLijst themas={sociaal} className="font-semibold" /> : null}
          </>
        ) : (
          notPrepared
        )}
      </span>
      {/* On a narrow card its parts sit in its grid: the status beside the klas, the button
          under the rest. Only its buttons take the pointer; around them the card underneath does. */}
      <span className="pointer-events-none contents @2xl:ml-auto @2xl:flex @2xl:shrink-0 @2xl:items-center @2xl:gap-3 [&_button]:pointer-events-auto">
        {status ? <span className="col-start-2 row-start-1 justify-self-end">{status}</span> : null}
        {cel.status === "gegeven" ? (
          // The card itself opens the given les; this only says so.
          <span className="text-primary px-2 py-1 text-sm font-semibold @max-2xl:hidden">
            {m["lessen.lesdag.bekijk_scores"]()}
          </span>
        ) : cel.status === "bezig" ? (
          <Button size="lg" className={actionClassName} onClick={onNaarLes}>
            {m["lessen.lesdag.naar_les"]()}
          </Button>
        ) : null}
      </span>
    </div>
  );
}
